import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { X, ArrowUpRight, TrendingUp, TrendingDown, Wallet, Circle } from "lucide-react";

const STATUS_TONE = {
  Planning: "muted", Production: "warn", Scheduled: "mint",
  Released: "mint", Wrapped: "muted",
  Watching: "muted", Contacted: "warn", "In Talks": "mint", Signed: "mint", Passed: "muted",
  Shortlisted: "warn", Approved: "mint", Discard: "muted",
  Proposed: "muted", "Under Review": "warn", Approved_: "mint", Archived: "muted",
  Pending: "warn", Signed_: "mint", Expired: "muted",
  Draft: "muted", Confirmed: "mint",
  Idea: "muted", Scripting: "warn", Ready: "mint", Posted: "mint",
};

// Config per stat card: how to fetch & how each row navigates
const STAT_CONFIGS = {
  releases: {
    title: "Releases In Motion",
    subtitle: "Everything currently between Planning and Scheduled",
    dept: "release_planning",
    modulePath: "release-planning",
    filter: (e) => ["Planning","Production","Scheduled"].includes(e.data.status),
    labelField: "title",
    metaFields: ["artist", "release_date", "type"],
  },
  artists: {
    title: "Artists on Roster",
    subtitle: "Everyone currently in your Artist Database",
    dept: "artist_database",
    modulePath: "artists",
    filter: () => true,
    labelField: "name",
    metaFields: ["role", "spotify"],
  },
  released: {
    title: "Released Songs",
    subtitle: "Songs that have shipped",
    dept: "released_songs",
    modulePath: "released-songs",
    filter: () => true,
    labelField: "title",
    metaFields: ["release_date"],
  },
  marketing: {
    title: "Marketing Campaigns",
    subtitle: "Every campaign on record",
    dept: "marketing",
    modulePath: "marketing",
    filter: () => true,
    labelField: "campaign",
    metaFields: ["channel", "linked_release", "budget"],
  },
  net: {
    title: "Net Position",
    subtitle: "Latest Revenue vs Expense entries in Finance",
    dept: "finance",
    modulePath: "finance",
    filter: () => true,
    labelField: "description",
    metaFields: ["vendor", "type", "amount"],
  },
  pending: {
    title: "Pending Approvals",
    subtitle: "Everything awaiting an admin decision across Future / Demo / Legal / Royalty",
    multi: [
      { dept: "future_strategy", modulePath: "future", labelField: "title", metaFields: ["category","priority"], approvals: ["Proposed","Under Review"] },
      { dept: "demo_submissions", modulePath: "demo-submissions", labelField: "song", metaFields: ["producer","date"], approvals: ["Shortlisted"] },
      { dept: "legal_tracker", modulePath: "legal", labelField: "document_type", metaFields: ["date"], approvals: ["Pending"] },
      { dept: "royalty_splits", modulePath: "royalty", labelField: "song_title", metaFields: [], approvals: ["Draft"] },
    ],
  },
};

export default function StatDetailModal({ statKey, onClose }) {
  const cfg = STAT_CONFIGS[statKey];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [artistMap, setArtistMap] = useState({});
  const [releaseMap, setReleaseMap] = useState({});
  const nav = useNavigate();

  // Load artist + release maps once per modal open so meta fields that store
  // UUIDs (artist, paid_for_artist_id, linked_release, …) render as readable
  // names instead of raw IDs. Falls back gracefully if a dept isn't accessible.
  useEffect(() => {
    (async () => {
      try {
        const a = await api.entries.list("artist_database");
        const am = {};
        (a.entries || []).forEach(e => { am[e.id] = e.data?.name || ""; });
        setArtistMap(am);
      } catch { /* non-admin may not have access — just leave empty */ }
      try {
        const rm = {};
        const [rp, rs] = await Promise.all([
          api.entries.list("release_planning").catch(() => ({ entries: [] })),
          api.entries.list("released_songs").catch(() => ({ entries: [] })),
        ]);
        [...(rp.entries || []), ...(rs.entries || [])].forEach(e => {
          rm[e.id] = e.data?.title || e.data?.song_title || "";
        });
        setReleaseMap(rm);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    if (!cfg) return;
    (async () => {
      setLoading(true);
      try {
        if (cfg.multi) {
          const all = await Promise.all(cfg.multi.map(async (m) => {
            try {
              const r = await api.entries.list(m.dept);
              return (r.entries || [])
                .filter((e) => m.approvals.includes(e.data.status))
                .map((e) => ({ ...e, __mod: m }));
            } catch { return []; }
          }));
          setRows(all.flat());
        } else {
          const r = await api.entries.list(cfg.dept);
          setRows((r.entries || []).filter(cfg.filter));
        }
      } catch { setRows([]); }
      setLoading(false);
    })();
  }, [statKey]);

  if (!cfg) return null;

  const go = (row) => {
    const mod = row.__mod || { modulePath: cfg.modulePath };
    onClose();
    nav(`/dashboard/${mod.modulePath}?highlight=${row.id}`);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "grid", placeItems: "center", padding: 20, overflowY: "auto" }} data-testid="stat-modal">
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }} />
      <div className="panel" style={{ position: "relative", width: "min(720px, 96vw)", maxHeight: "82vh", overflowY: "auto", margin: "auto", padding: 0 }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
          padding: "20px 24px 14px",
          position: "sticky", top: 0, zIndex: 2,
          background: "var(--panel)",
          borderBottom: "1px solid var(--border)",
          borderTopLeftRadius: "inherit", borderTopRightRadius: "inherit",
        }}>
          <div>
            <div className="muted" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>Current positioning</div>
            <h3 style={{ fontSize: 20, margin: "4px 0 0 0" }}>{cfg.title}</h3>
            <p className="muted" style={{ fontSize: 13, margin: "6px 0 0 0", lineHeight: 1.5 }}>{cfg.subtitle}</p>
          </div>
          <button className="btn" onClick={onClose} data-testid="stat-modal-close"><X size={14} /></button>
        </div>

        <div style={{ padding: "18px 24px 24px" }}>
        {loading ? (
          <div className="muted" style={{ padding: 40, textAlign: "center" }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div className="muted" style={{ padding: 40, textAlign: "center", fontSize: 13 }}>Nothing here right now.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {rows.map((row) => {
              const mod = row.__mod || cfg;
              const label = row.data?.[mod.labelField] || "(untitled)";
              // Field keys that store a UUID — resolve to a readable name/title
              // via the maps loaded on mount. Any key ending in `_artist_id`
              // (or the plain `artist` field) is treated as an artist ref;
              // anything ending in `_release`, `_song_id`, `linked_release`
              // etc. is treated as a release ref.
              const resolve = (k, v) => {
                if (v == null || v === "") return v;
                if (Array.isArray(v)) return v.map(x => resolve(k, x)).join(", ");
                if (typeof v !== "string") return v;
                if (k === "artist" || k.endsWith("_artist_id") || k === "artists" || k === "lyricists")
                  return artistMap[v] || v;
                if (k.endsWith("_release") || k.endsWith("_song_id") || k === "related_release")
                  return releaseMap[v] || v;
                return v;
              };
              const meta = (mod.metaFields || [])
                .map((k) => resolve(k, row.data?.[k]))
                .filter((v) => v != null && v !== "")
                .map((v) => Array.isArray(v) ? v.join(", ") : (typeof v === "number" ? v.toLocaleString("en-IN") : String(v)))
                .join(" · ");
              const status = row.data?.status;
              const tone = STATUS_TONE[status] || "muted";
              const toneColor = tone === "mint" ? "var(--mint)" : tone === "warn" ? "var(--warn)" : "var(--muted)";
              return (
                <button
                  key={row.id}
                  onClick={() => go(row)}
                  data-testid={`stat-row-${row.id}`}
                  className="clickable"
                  style={{ background: "var(--panel2)", border: "1px solid var(--border)", borderRadius: 10, padding: 14, textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      {status && <Circle size={7} style={{ color: toneColor, fill: toneColor }} />}
                      <div style={{ fontWeight: 500, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</div>
                    </div>
                    {(meta || status) && (
                      <div className="muted" style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        {status && <span className="badge" style={{ padding: "1px 7px", fontSize: 10, color: toneColor, borderColor: toneColor }}>{status}</span>}
                        {meta && <span>{meta}</span>}
                        {row.__mod && <span style={{ opacity: 0.6 }}>· {moduleLabel(row.__mod.modulePath)}</span>}
                      </div>
                    )}
                  </div>
                  <ArrowUpRight size={15} style={{ color: "var(--muted)", flexShrink: 0 }} />
                </button>
              );
            })}
          </div>
        )}

        {cfg.dept === "finance" && !loading && rows.length > 0 && <NetSummary rows={rows} />}
        </div>
      </div>
    </div>
  );
}

function NetSummary({ rows }) {
  let rev = 0, exp = 0;
  rows.forEach(r => {
    const a = Number(r.data?.amount || 0);
    if (r.data?.type === "Revenue") rev += a;
    if (r.data?.type === "Expense") exp += a;
  });
  const net = rev - exp;
  return (
    <div style={{ marginTop: 16, padding: 14, background: "var(--panel2)", border: "1px solid var(--border)", borderRadius: 10, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
      <MiniStat label="Revenue" value={rev} tone="mint" icon={<TrendingUp size={11} />} />
      <MiniStat label="Expense" value={exp} tone="warn" icon={<TrendingDown size={11} />} />
      <MiniStat label="Net" value={net} tone={net >= 0 ? "mint" : "danger"} icon={<Wallet size={11} />} />
    </div>
  );
}
function MiniStat({ label, value, tone, icon }) {
  const color = tone === "mint" ? "var(--mint)" : tone === "warn" ? "var(--warn)" : tone === "danger" ? "var(--danger)" : "var(--text)";
  return (
    <div>
      <div className="muted" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", display: "flex", alignItems: "center", gap: 4 }}>{icon} {label}</div>
      <div style={{ fontFamily: "Space Grotesk", fontWeight: 700, fontSize: 18, color, marginTop: 2 }}>₹ {Math.round(value).toLocaleString("en-IN")}</div>
    </div>
  );
}
function moduleLabel(p) {
  return {
    "future": "Future Strategy",
    "demo-submissions": "Demo Submissions",
    "legal": "Legal Tracker",
    "royalty": "Royalty Splits",
  }[p] || p;
}
